import React, { useEffect, useState } from "react";
import { MapPin, TrendingUp, Users, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/common/data-table";
import { MetricCard } from "@/components/common/metric-card";
import { formatNumber, formatDuration, formatPercent } from "@/lib/utils";
import { analyticsAPI } from "@/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#E86E30", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

interface StateData {
  state: string;
  total_sessions: number;
  unique_plates: number;
  avg_duration_minutes: number;
  estimated_revenue: number;
}

export function StateAnalytics() {
  const [stateData, setStateData] = useState<StateData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStateData = async () => {
      try {
        setIsLoading(true);
        // Use last 30 days as default range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        const response = await analyticsAPI.getStateAnalytics({
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
        });
        // Map API response to StateData format
        const data = response.data?.data || [];
        setStateData(
          data.map((s) => ({
            state: s.state,
            total_sessions: s.total_sessions,
            unique_plates: s.unique_plates,
            avg_duration_minutes: s.average_duration || 0,
            estimated_revenue: 0,
          })),
        );
      } catch (error) {
        console.error("Failed to fetch state analytics:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStateData();
  }, []);

  // Calculate totals
  const totalSessions = stateData.reduce((sum, s) => sum + s.total_sessions, 0);
  const totalUniquePlates = stateData.reduce(
    (sum, s) => sum + s.unique_plates,
    0,
  );
  const avgDuration =
    stateData.length > 0
      ? stateData.reduce((sum, s) => sum + s.avg_duration_minutes, 0) /
        stateData.length
      : 0;

  // Transform data for table
  const tableData = stateData.map((s) => ({
    state: s.state,
    sessions: s.total_sessions,
    unique_plates: s.unique_plates,
    avg_duration: s.avg_duration_minutes,
    percentage:
      totalSessions > 0 ? (s.total_sessions / totalSessions) * 100 : 0,
    revenue: s.estimated_revenue,
  }));

  const columns = [
    {
      id: "state",
      header: "State",
      accessorKey: "state",
      cell: ({ row }: any) => (
        <div className="flex items-center space-x-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.state}</span>
        </div>
      ),
    },
    {
      id: "sessions",
      header: "Total Sessions",
      accessorKey: "sessions",
      cell: ({ row }: any) => formatNumber(row.original.sessions),
    },
    {
      id: "unique_plates",
      header: "Unique Plates",
      accessorKey: "unique_plates",
      cell: ({ row }: any) => formatNumber(row.original.unique_plates),
    },
    {
      id: "avg_duration",
      header: "Avg Duration",
      accessorKey: "avg_duration",
      cell: ({ row }: any) => formatDuration(row.original.avg_duration),
    },
    {
      id: "percentage",
      header: "Percentage",
      accessorKey: "percentage",
      cell: ({ row }: any) => formatPercent(row.original.percentage),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">State Analytics</h1>
        <p className="text-muted-foreground">
          Analysis of parking sessions by license plate state
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total States"
          value={stateData.length}
          description="States represented"
          icon={MapPin}
        />
        <MetricCard
          title="Total Sessions"
          value={formatNumber(totalSessions)}
          description="Across all states"
          icon={TrendingUp}
        />
        <MetricCard
          title="Unique Plates"
          value={formatNumber(totalUniquePlates)}
          description="Distinct vehicles"
          icon={Users}
        />
        <MetricCard
          title="Average Duration"
          value={formatDuration(avgDuration)}
          description="Overall average"
          icon={Clock}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Sessions by State</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tableData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="state"
                    className="text-xs fill-muted-foreground"
                  />
                  <YAxis className="text-xs fill-muted-foreground" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="sessions" fill="#E86E30" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>State Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...tableData].sort(
                    (a, b) => b.percentage - a.percentage,
                  )}
                  layout="vertical"
                  margin={{ left: 30 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    type="number"
                    unit="%"
                    className="text-xs fill-muted-foreground"
                    domain={[0, "dataMax"]}
                  />
                  <YAxis
                    type="category"
                    dataKey="state"
                    className="text-xs fill-muted-foreground"
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    formatter={(value: any, name: string) => {
                      if (name === "percentage") return `${value.toFixed(1)}%`;
                      return formatNumber(value);
                    }}
                    labelFormatter={(label) => `State: ${label}`}
                  />
                  <Bar
                    dataKey="percentage"
                    fill="#E86E30"
                    radius={[0, 4, 4, 0]}
                  >
                    {tableData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>State Analytics Table</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={tableData}
            loading={isLoading}
            searchPlaceholder="Search states..."
            searchColumn="state"
          />
        </CardContent>
      </Card>
    </div>
  );
}
